import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { getConsent, saveConsent } from "@/lib/consent";

const CATEGORIES = [
  { id: "necessary", label: "Técnicas (necesarias)", desc: "Sesión, seguridad y preferencias. Imprescindibles para que la web funcione. No requieren consentimiento.", locked: true },
  { id: "analytics", label: "Analíticas", desc: "Nos ayudan a entender cómo se usa la web (páginas vistas, conversiones) para mejorarla. Incluye PostHog." },
  { id: "marketing", label: "Publicidad / marketing", desc: "Medición de campañas y personalización de anuncios. Actualmente no usamos ninguna, pero puedes dejarlas desactivadas." },
];

export default function CookieBanner() {
  const [visible, setVisible] = useState(() => !getConsent());
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const onOpen = () => {
      const c = getConsent();
      setPrefs({ analytics: !!c?.analytics, marketing: !!c?.marketing });
      setOpen(true);
    };
    window.addEventListener("ap-open-cookie-settings", onOpen);
    return () => window.removeEventListener("ap-open-cookie-settings", onOpen);
  }, []);

  const decide = (p) => {
    saveConsent(p);
    setVisible(false);
    setOpen(false);
  };

  return (
    <>
      {visible && !open && (
        <div data-testid="cookie-banner" role="dialog" aria-live="polite" aria-label="Aviso de cookies"
          className="fixed bottom-0 inset-x-0 z-[60] sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md bg-slate-900 text-slate-200 border-t-4 sm:border-t-0 sm:border-l-4 border-orange-600 shadow-2xl p-5 animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-2 text-white font-display font-bold text-base">
            <Cookie className="w-5 h-5 text-orange-500" /> Usamos cookies
          </div>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Usamos cookies técnicas necesarias y, solo si lo aceptas, cookies analíticas para mejorar el servicio.
            Puedes aceptarlas, rechazarlas o configurarlas. Más info en nuestra{" "}
            <Link to="/politica-de-cookies" className="text-orange-400 underline" data-testid="cookie-banner-policy-link">Política de Cookies</Link>.
          </p>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button data-testid="cookie-reject-all" onClick={() => decide({ analytics: false, marketing: false })}
              className="h-11 px-2 border border-slate-600 text-white text-sm font-bold hover:border-white transition-colors">Rechazar todas</button>
            <button data-testid="cookie-configure" onClick={() => { setPrefs({ analytics: false, marketing: false }); setOpen(true); }}
              className="h-11 px-2 border border-slate-600 text-white text-sm font-bold hover:border-white transition-colors">Configurar</button>
            <button data-testid="cookie-accept-all" onClick={() => decide({ analytics: true, marketing: true })}
              className="btn-brand h-11 px-2 text-sm font-bold">Aceptar todas</button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl">Configurar cookies</DialogTitle>
            <DialogDescription>Elige qué categorías permites. Las no necesarias están desactivadas por defecto.</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-slate-200 border border-slate-200">
            {CATEGORIES.map((c) => (
              <div key={c.id} className="p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="font-bold text-sm">{c.label}</div>
                  <p className="text-xs text-slate-500 mt-1">{c.desc}</p>
                </div>
                <Switch data-testid={`cookie-switch-${c.id}`} checked={c.locked ? true : prefs[c.id]} disabled={c.locked}
                  onCheckedChange={(v) => setPrefs({ ...prefs, [c.id]: v })} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="cookie-dialog-reject" onClick={() => decide({ analytics: false, marketing: false })}
              className="h-11 border border-slate-300 font-bold text-sm hover:border-slate-900 transition-colors">Rechazar todas</button>
            <button data-testid="cookie-save-prefs" onClick={() => decide(prefs)} className="btn-brand h-11 font-bold text-sm">Guardar preferencias</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
